import { Textarea, ActionIcon } from '@mantine/core';
import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useState, useRef, useEffect } from 'react';
import { IconArrowBackUp } from '@tabler/icons-react';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import OperationControl from '../OperationControl';

export default function AIOpInPlaceEdit({
  operation,
  testContentId,
  stepOrder,
  previewCursorAllowed,
  readonly,
  disableMenu,
  parentContext,
  badge,
  gearIconContent,
  prompt,
  placeholderText,
  onPromptUpdate,
  additionalIcons,
}: {
  operation: SelectedOperation;
  testContentId: string;
  stepOrder: number;
  previewCursorAllowed: boolean;
  readonly?: boolean;
  disableMenu?: boolean;
  parentContext?: OperationParentContext;

  // Customizable props
  badge: ReactNode;
  gearIconContent: ReactNode;
  prompt: string;
  placeholderText: string;
  onPromptUpdate: (prompt: string) => Promise<void>;
  additionalIcons?: ReactNode;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [animationParent] = useAutoAnimate({ easing: 'ease-in-out', duration: 200 });

  const handleEdit = () => {
    if (readonly) {
      return;
    }
    setEditValue(prompt);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim() !== prompt) {
      void onPromptUpdate(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(prompt);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    }
  };

  // Auto-focus when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  return (
    <div
      className="flex w-full flex-col items-start py-[0.2rem]"
      ref={animationParent}
    >
      {isEditing ?
        <div
          key="editing"
          className="w-full p-2"
        >
          <div className="mb-2 flex items-center gap-2">
            {badge}
            <div className="flex items-center gap-1">
              <ActionIcon
                size="xs"
                variant="light"
                radius="xl"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleCancel}
                aria-label="Undo"
                className="bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
              >
                <IconArrowBackUp size={12} />
              </ActionIcon>
              {additionalIcons}
            </div>
          </div>
          <Textarea
            ref={textareaRef}
            value={editValue}
            onChange={(event) => setEditValue(event.currentTarget.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            placeholder={placeholderText}
            autosize
            minRows={2}
            maxRows={10}
            classNames={{
              input: 'resize-none',
            }}
          />
        </div>
      : <div
          key="view"
          className="group relative w-full"
        >
          <div className="w-full rounded-md px-1 py-2 hover:bg-gray-100 hover:dark:bg-gray-800">
            <div className="flex w-full items-center gap-2 pr-2">
              {badge}
              <div className="flex items-center gap-2">
                {gearIconContent}
                {additionalIcons}
              </div>
            </div>
            <div
              className="mt-1 flex-grow cursor-text pr-6 pl-1 text-sm break-words whitespace-pre-wrap"
              onClick={handleEdit}
            >
              {prompt || (
                <span className="text-gray-500">{readonly ? 'The prompt has not been set' : placeholderText}</span>
              )}
            </div>
          </div>
          {!disableMenu && (
            <div
              className={clsx(
                'absolute top-1/2 right-1 -translate-y-1/2 opacity-0 transition-opacity delay-200 group-hover:opacity-100',
                readonly ? 'invisible' : '',
              )}
            >
              <OperationControl
                operation={operation}
                previewCursorAllowed={previewCursorAllowed}
                testContentId={testContentId}
                stepOrder={stepOrder}
                parentContext={parentContext}
              />
            </div>
          )}
        </div>
      }
    </div>
  );
}
